import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormField } from '@/components/molecules/FormField';
import Button from '@/components/atoms/Button';
import Card from '@/components/atoms/Card';
import { apiService } from '@/services/api';
import { validateEmail } from '@/utils/validation';
import '@/components/organisms/LoginForm/LoginForm.scss';
import './Login.scss';

/**
 * Step 1 of password recovery: the person types their email and the backend
 * sends a link to /reset-password. The answer is the same whether or not the
 * email has an account, so it never reveals who is registered.
 */
const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [sentMessage, setSentMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validateEmail(email.trim(), t);
    if (validationError) {
      setError(validationError);
      return;
    }
    setIsLoading(true);
    setError(undefined);
    try {
      const response = await apiService.forgotPassword(email.trim().toLowerCase());
      setSentMessage(response.message || t('auth.forgotPassword.sent'));
    } catch (requestError: any) {
      setError(requestError.response?.data?.detail || t('auth.forgotPassword.error'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-page__content">
        <Card variant="elevated" padding="lg" className="login-form">
          <div className="login-form__header">
            <h2 className="login-form__title">{t('auth.forgotPassword.title')}</h2>
            <p className="login-form__subtitle">{t('auth.forgotPassword.subtitle')}</p>
          </div>

          {sentMessage ? (
            <p className="login-form__notice" role="status">{sentMessage}</p>
          ) : (
            <form onSubmit={handleSubmit} className="login-form__form">
              <FormField
                id="forgot-password-email"
                label={t('auth.forgotPassword.email')}
                type="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(undefined);
                }}
                error={error}
                required
                autoComplete="email"
                autoFocus
              />
              <div className="login-form__actions">
                <Button type="submit" variant="primary" size="md" fullWidth loading={isLoading} disabled={isLoading}>
                  {t('auth.forgotPassword.submit')}
                </Button>
              </div>
            </form>
          )}

          <div className="login-page__footer">
            <p className="login-page__register-text">
              <Link to="/reset-password" className="login-page__register-link">
                {t('auth.forgotPassword.haveToken')}
              </Link>
            </p>
            <p className="login-page__home-text">
              <Link to="/login" className="login-page__home-link">
                {t('auth.forgotPassword.backToLogin')}
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
